import Timeout from "await-timeout";

// A completed NOL order reaches the legacy booking table through the POS sync
// queue. Until the sync worker has created that booking, the order-keyed
// endpoints answer HTTP 409, which the POS retries for a short while.

type RetryPredicate = (err: unknown) => boolean;

interface IHttpErrorLike {
  response?: {
    status?: number;
    data?: { error?: { details?: { reason?: string } } };
  };
}

/** HTTP status of an axios error, or undefined for a network/non-HTTP error. */
export function getHttpStatus(err: unknown): number | undefined {
  return (err as IHttpErrorLike | undefined)?.response?.status;
}

/**
 * Reason the services API puts in `error.details.reason` of its error body
 * (`{ data: null, error: { code, message, details } }`).
 *
 * @returns the reason, or undefined for a network error or a non-services body
 */
export function getServicesErrorReason(err: unknown): string | undefined {
  return (err as IHttpErrorLike | undefined)?.response?.data?.error?.details
    ?.reason;
}

/** Main API order-based endpoints: any 409 means NOT_SYNCED. */
const isConflict: RetryPredicate = (err) => getHttpStatus(err) === 409;

/**
 * Run `fn`, retrying while `isRetryable(err)` holds, up to `retries` extra
 * attempts spaced by `delayMs`. Any other error, or the last retryable one, is
 * rethrown unchanged.
 */
export async function withNotSyncedRetry<T>(
  fn: () => Promise<T>,
  isRetryable: RetryPredicate = isConflict,
  retries = 4,
  delayMs = 3000
): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await fn();
    } catch (err) {
      if (isRetryable(err) && attempt < retries) {
        await Timeout.set(delayMs);
        continue;
      }
      throw err;
    }
  }
}
